import { useContext, useState, useEffect } from 'react';
import B2BInput from '../../components/common/B2BInput';
import { ProductContext } from '../../Context/ProductContext';
import './ProductDimension.css';

const ProductDimension = ({ isCondition }) => {
    const { product, handleChange, inputError, setProduct, setInputError } = useContext(ProductContext);
    const [width, setWidth] = useState({
        inches: '',
        centimeter: '',
        meter: '',
        meter_per_kg: '',
    });
    const [uom, setUom] = useState({
        kg: '',
        meter_per_roll: '',
        yard_per_roll: '',
    });

    useEffect(() => {
        if (product.metrics?.width) {
            updateWidthValues(product.metrics.width);
        }
    }, [product.metrics?.width]);

    const updateWidthValues = (widthValue) => {
        const newWidth = {
            inches: widthValue ? parseFloat(widthValue).toFixed(2) : '',
            centimeter: widthValue ? (widthValue * 2.54).toFixed(2) : '',
            meter: widthValue ? (widthValue * 0.0254).toFixed(2) : '',
        };
        const weight = product.metrics?.weight || 1;
        const meterPerKgValue = widthValue && weight ? (1000 / (newWidth.meter * weight)).toFixed(2) : '';
        newWidth.meter_per_kg = isNaN(meterPerKgValue) ? '' : meterPerKgValue;

        setWidth(newWidth);

        setProduct((prevState) => {
            const updatedMetrics = {
                ...prevState.metrics,
                width: newWidth.inches ? parseFloat(newWidth.inches) : prevState.metrics.width,
                inches: newWidth.inches ? parseFloat(newWidth.inches) : prevState.metrics.inches,
                centimeter: newWidth.centimeter ? parseFloat(newWidth.centimeter) : prevState.metrics.centimeter,
                meter: newWidth.meter ? parseFloat(newWidth.meter) : prevState.metrics.meter,
                meterPerKg: newWidth.meter_per_kg ? parseFloat(newWidth.meter_per_kg) : prevState.metrics.meterPerKg,
            };
            return {
                ...prevState,
                metrics: updatedMetrics,
            };
        });

        if (product.otherInformation?.kilogram) {
            const kgValue = parseFloat(product.otherInformation.kilogram);
            const meters = kgValue * newWidth.meter_per_kg;
            const yards = meters * 1.094;
            setUom((prevUom) => ({
                ...prevUom,
                kg: kgValue.toString(),
                meter_per_roll: isNaN(meters) ? '' : meters.toFixed(2),
                yard_per_roll: isNaN(yards) ? '' : yards.toFixed(2),
            }));
        }
    };



    const handleWeightChange = (event) => {
        const newWeight = parseFloat(event.target.value) || 0;
        handleChange(event, 'metrics.weight', 'weightErrorMessage');

        const meterValue = width.meter;
        const newMeterPerKg = meterValue && newWeight ? (1000 / (meterValue * newWeight)).toFixed(2) : '';

        setWidth((prevWidth) => {
            const updatedWidth = {
                ...prevWidth,
                meter_per_kg: isNaN(newMeterPerKg) ? '' : newMeterPerKg,
            };
            return updatedWidth;
        });

        setProduct((prevState) => {
            const updatedMetrics = {
                ...prevState.metrics,
                weight: newWeight,
                meterPerKg: newMeterPerKg ? parseFloat(newMeterPerKg) : prevState.metrics.meterPerKg,
            };
            return {
                ...prevState,
                metrics: updatedMetrics,
            };
        });
    };

    const handleUOMChange = (event, uomType) => {
        const newValue = event.target.value;
        setUom((prevUom) => {
            let updatedUom = { ...prevUom, [uomType]: newValue };
            if (uomType === 'kg') {
                const kgValue = parseFloat(newValue) || 0;
                const meters = kgValue * width.meter_per_kg;
                updatedUom.meter_per_roll = isNaN(meters) ? '' : meters.toFixed(2);
                const yards = meters * 1.094;
                updatedUom.yard_per_roll = isNaN(yards) ? '' : yards.toFixed(2);
                setProduct((prevState) => {
                    const updatedProduct = {
                        ...prevState,
                        otherInformation: {
                            ...prevState.otherInformation,
                            kilogram: kgValue || null,
                        },
                    };
                    return updatedProduct;
                });
            }
            return updatedUom;
        });
    };

    const dimension_json = [
        {
            label: 'Swatch Available',
            type: 'checkbox',
            value: product.vendorProductInfo?.swatchAvailable || false,
            onChange: (value) => handleChange({ target: { type: 'checkbox', checked: value } }, 'vendorProductInfo.swatchAvailable', 'swatchAvailableError'),
            error: inputError?.swatchAvailableError || '',
        },
        {
            label: 'Sample Available',
            type: 'checkbox',
            value: product.vendorProductInfo?.sampleAvailable || false,
            onChange: (value) => handleChange({ target: { type: 'checkbox', checked: value } }, 'vendorProductInfo.sampleAvailable', 'sampleAvailableError'),
            error: inputError?.sampleAvailableError || '',
        },
      
    ];

    const uom_json = [
        {
            label: product.otherInformation.unitOfMeasures?.isKg
                ? 'Std.kg'
                : product.otherInformation.unitOfMeasures?.isMeter
                    ? 'Std.Meter'
                    : product.otherInformation.unitOfMeasures?.isYard
                        ? 'Std.Yard'
                        : '',
            type: 'number',
            placeholder: product.otherInformation.unitOfMeasures?.isKg
                ? 'In kg'
                : product.otherInformation.unitOfMeasures?.isMeter
                    ? 'In Meter'
                    : product.otherInformation.unitOfMeasures?.isYard
                        ? 'In Yard'
                        : '',
            value: uom.kg,
            onChange: (event) => handleUOMChange(event, 'kg'),
            disable: false,
        },
        {
            label: 'Std. kg',
            type: 'text',
            placeholder: 'Enter Std. kg',
            value: product?.vendorProductInfo?.standardRollWt || product?.standardRollWt || '',
            onChange: (event) =>
                setProduct((prev) => ({
                    ...prev,
                    standardRollWt: event.target.value,
                    vendorProductInfo: {
                        ...prev.vendorProductInfo,
                        standardRollWt: event.target.value,
                    },
                })),
            disable: false,
        },
        {
            label: 'Cone Weight (grms)',
            type: 'text',
            placeholder: 'In grm',
            value: product?.otherInformation?.coneWeight || product?.vendorProductInfo?.coneWeight || '',
            onChange: (event) =>
                setProduct((prev) => ({
                    ...prev,
                    otherInformation: {
                        ...prev.otherInformation,
                        coneWeight: event.target.value,
                    },
                    vendorProductInfo: {
                        ...prev.vendorProductInfo,
                        coneWeight: event.target.value,
                    },
                })),
            disable: Boolean(product?.orderDriven) || !product?.otherInformation?.unitOfMeasures?.isKg,
        },
    ];


    return (
        <form  >
            <div className="dimensions-section-dim">
                <div className="dimensions-griddd">
                    {dimension_json.map((field, index) => (
                        <div key={index} className="dimension-group-dim">
                            <label className="dimension-label">
                                {field.label} <span className="error-message">*</span>
                            </label>
                            <div className="input-wrapper">
                                {field.type === 'checkbox' ? (
                                    <div className="button-wrapper">
                                        <button
                                            type="button"
                                            className={`toggle-button ${field.value === true ? 'active' : ''}`}
                                            style={{ backgroundColor: field.value === true ? '#8BC53F' : 'lightgray', borderColor: field.value === true ? '#8BC53F' : 'lightgray', cursor: isCondition ? 'not-allowed' : 'pointer' }}
                                            onClick={() => field.onChange(true)}
                                            disabled={isCondition}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            className={`toggle-button ${field.value === false ? 'active' : ''}`}
                                            style={{ backgroundColor: field.value === false ? '#8BC53F' : 'lightgray', borderColor: field.value === false ? '#8BC53F' : 'lightgray', cursor: isCondition ? 'not-allowed' : 'pointer' }}
                                            onClick={() => field.onChange(false)}
                                            disabled={isCondition}
                                        >
                                            No
                                        </button>
                                        {field.error && <span className="error-message">{field.error}</span>}
                                    </div>
                                ) : (
                                    <B2BInput
                                        label={field.label === 'Price' ? '' : 'mm'}
                                        value={field.value}
                                        type={field.type}
                                        required
                                        disabled={isCondition}
                                        placeholder={field.placeholder}
                                        onChange={field.onChange}
                                        error={field.error}
                                        withAsterisk={false}
                                    />
                                )}
                                {inputError?.coneWeightErrorMessage && (
                                    <span className="error-message">{inputError.coneWeightErrorMessage}</span>
                                )}
                            </div>
                        </div>
                    ))}

                </div>
            </div>
        </form>
    );
};

export default ProductDimension;